/**
 * ProfileBio — renders the user's tagline, bio paragraph and topic/tag pills.
 *
 * Pure presentational: User in, JSX out. No hooks or stores.
 * Returns null when all three are empty.
 */
import styled from 'styled-components';
import type { User } from '@/core/models';

/* ── Styled components ──────────────────────────────────────────────────── */

const BioRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BioText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: ${({ theme }) => theme.lineHeight.md};
  max-width: 68ch;
  overflow-wrap: anywhere;
`;

/* 1.27.70: the one-line tagline from Edit Profile, set above the bio. */
const Tagline = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: ${({ theme }) => theme.lineHeight.md};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  max-width: 68ch;
  overflow-wrap: anywhere;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
`;

/* ── Component ──────────────────────────────────────────────────────────── */

export interface ProfileBioProps {
  user: User;
}

export function ProfileBio({ user }: ProfileBioProps) {
  const pills = user.topicPills ?? user.tags ?? [];
  const hasBio = Boolean(user.bio);
  const hasPills = pills.length > 0;
  const tagline = user.tagline?.trim();

  if (!hasBio && !hasPills && !tagline) return null;

  return (
    <BioRoot>
      {tagline && <Tagline data-testid="profile-tagline">{tagline}</Tagline>}
      {hasBio && <BioText>{user.bio}</BioText>}
      {hasPills && (
        <PillRow>
          {pills.map((pill) => (
            <Pill key={pill}>{pill}</Pill>
          ))}
        </PillRow>
      )}
    </BioRoot>
  );
}
